import { set, get, upperFirst, omit } from 'lodash'
import moment from 'moment'
import omitEmpty from 'omit-empty'
import {
  SCHOLARSHIP_PATH,
  DATE_DATA_FORMAT,
  MINIMUM_GPA,
  SCHOLARSHIPS,
  STAGED_SCHOLARSHIPS,
  MINIMUM_CLASS_RANK,
  MINIMUM_OVERALL_SAT,
  MINIMUM_OVERALL_ACT,
  MAXIMUM_AGI,
  MAXIMUM_EFC
} from 'utils/constants'
import { toDate } from 'utils/toDate'

const DATE = 'DATE'
const props = [
  ['openDate', DATE],
  ['closeDate', DATE],
  ['announcementDate', DATE],
  ['programName'],
  ['scholarshipMaximumAward'],
  ['scholarshipMinimumAward'],
  ['programSelfDescription'],
  ['numberOfScholarshipsAwarded'],
  ['programURL'],
  ['applicationURL'],
  ['isContest'],
  ['isMeritBased'],
  ['isNeedBased'],
  ['programReferenceId'],
]

export const serialize = (formData, program, source = SCHOLARSHIPS) => {
  const { organization: { id: orgId } } = program
  const scholarshipId = source === SCHOLARSHIPS ? program.id : program.scholarship?.id
  const { applicationType,
          academicEligibilityMinGPA,
          enrollmentStatus,
          academicEligibilityMinClassRank,
          academicEligibilityMinActComposite,
          academicEligibilityMinOverallSat,
          financialEligibilityMaximumAgi,
          financialEligibilityMaximumEfc
  } = formData

  const json = omitEmpty(
    Object.keys(formData).reduce((acc, current) => {
      const schemaProp = upperFirst(current)
      const propValue = formData[current]

      // Parse dates
      const isDate = !!props.find(([prop, dataType]) => prop === current && dataType === DATE)
      if (isDate) {
        acc[schemaProp] = propValue
          ? moment(propValue).format(DATE_DATA_FORMAT)
          : null
      } else {
        acc[schemaProp] = propValue
      }

      return acc
    }, {}),
  )

  //Add the minimum GPA
  if (academicEligibilityMinGPA) {
    json.EligibilityCriteria = {
      ...json.EligibilityCriteria,
      Academics: [
        ...json?.EligibilityCriteria?.Academics || '',
        {
          AcademicEligibility: MINIMUM_GPA,
          AcademicEligibilityValue: academicEligibilityMinGPA,
        },
      ],
    }
  }

  //Add Minimum class rank
  if (academicEligibilityMinClassRank) {
    json.EligibilityCriteria = {
      ...json.EligibilityCriteria,
      Academics: [
        ...json?.EligibilityCriteria?.Academics || '',
        {
          AcademicEligibility: MINIMUM_CLASS_RANK,
          AcademicEligibilityValue: academicEligibilityMinClassRank,
        },
      ],
    }
  }

  //Add Minimum Overall Sat
  if (academicEligibilityMinOverallSat) {
    json.EligibilityCriteria = {
      ...json.EligibilityCriteria,
      Academics: [
        ...json?.EligibilityCriteria?.Academics || '',
        {
          AcademicEligibility: MINIMUM_OVERALL_SAT,
          AcademicEligibilityValue: academicEligibilityMinOverallSat,
        },
      ],
    }
  }

  //Add Minimum Overall Act
  if (academicEligibilityMinActComposite) {
    json.EligibilityCriteria = {
      ...json.EligibilityCriteria,
      Academics: [
        ...json?.EligibilityCriteria?.Academics || '',
        {
          AcademicEligibility: MINIMUM_OVERALL_ACT,
          AcademicEligibilityValue: academicEligibilityMinActComposite,
        },
      ],
    }
  }

  //Add Maximum EFC
  if (financialEligibilityMaximumEfc) {
    json.EligibilityCriteria = {
      ...json.EligibilityCriteria,
      FinancialInformation: [
        ...json?.EligibilityCriteria?.FinancialInformation || '',
        {
          FinancialEligibilityAmount: financialEligibilityMaximumEfc,
          FinancialEligibilityCriteria: MAXIMUM_EFC
        },
      ],
    }
  }

  //Add Maximum AGI
  if (financialEligibilityMaximumAgi) {
    json.EligibilityCriteria = {
      ...json.EligibilityCriteria,
      FinancialInformation: [
        ...json?.EligibilityCriteria?.FinancialInformation || '',
        {
          FinancialEligibilityAmount: financialEligibilityMaximumAgi,
          FinancialEligibilityCriteria: MAXIMUM_AGI
        },
      ],
    }
  }

  //Add enrollment status
  if (enrollmentStatus) {
    json.EligibilityCriteria = {
      ...json.EligibilityCriteria,
      EnrollmentStatus: enrollmentStatus
    }
  }

  const fields = {
    orgId,
    applicationType,
    scholarshipId,
    json: set(
      {},
      SCHOLARSHIP_PATH,
      omit(json, [
        'ProgramOrganizationName',
        'ApplicationType',
        'AcademicEligibilityMinGPA',
        'EnrollmentStatus',
        'AcademicEligibilityMinClassRank',
        'AcademicEligibilityMinActComposite',
        'AcademicEligibilityMinOverallSat',
        'FinancialEligibilityMaximumAgi',
        'FinancialEligibilityMaximumEfc',
        'Id',
      ]),
    ),
  }

  const ecPath = `json.${SCHOLARSHIP_PATH}.EligibilityCriteria`
  const ec = get(fields, ecPath)
  if (ec) set(fields, ecPath, [ec])

  return { program: fields }
}

function getSwitchCase(source) {
  return source === STAGED_SCHOLARSHIPS ?
    (prop, { includePath = true } = {}) =>
      (includePath ? `${SCHOLARSHIP_PATH}.` : '') +
      prop.split('.').map(upperFirst).join('.')
    : v => v
}

export const deserialize = (data, source = SCHOLARSHIPS) => {
  const switchCase = getSwitchCase(source)

  const fields = props.reduce((acc, [prop, dataType]) => {
    if (dataType === DATE) {
      const dateProp = get(data, `json.${switchCase(prop)}`, null)
      if (dateProp) acc[prop] = toDate(dateProp)
    } else { // Others
      acc[prop] = get(data, `json.${switchCase(prop)}`, null)
    }
    return acc
  }, {})

  // specific fields
  fields['applicationType'] = data?.applicationType
  fields['programOrganizationName'] = data?.organization?.name
  const minGpa = get(data,
    `json.${switchCase('eligibilityCriteria.0.academics')}`, null)
    ?.find((item) => {
      const key = switchCase('academicEligibility', { includePath: false })
      return item[key] === MINIMUM_GPA
    })

  const key = switchCase('academicEligibilityValue', { includePath: false })

  const minClassRank = get(data,
    `json.${switchCase('eligibilityCriteria.0.academics')}`, null)
    ?.find((item) => {
      const key = switchCase('academicEligibility', { includePath: false })
      return item[key] === MINIMUM_CLASS_RANK
  })
  const minOverallSat = get(data,
    `json.${switchCase('eligibilityCriteria.0.academics')}`, null)
    ?.find((item) => {
      const key = switchCase('academicEligibility', { includePath: false })
      return item[key] === MINIMUM_OVERALL_SAT
  })
  const minOverallAct = get(data,
    `json.${switchCase('eligibilityCriteria.0.academics')}`, null)
    ?.find((item) => {
      const key = switchCase('academicEligibility', { includePath: false })
      return item[key] === MINIMUM_OVERALL_ACT
  })
  const maxAGI = get(data,
    `json.${switchCase('eligibilityCriteria.0.financialInformation')}`, null)
    ?.find((item) => {
      const key = switchCase('financialEligibilityCriteria', { includePath: false })
      return item[key] === MAXIMUM_AGI
  })
  const maxEFC = get(data,
    `json.${switchCase('eligibilityCriteria.0.financialInformation')}`, null)
    ?.find((item) => {
      const key = switchCase('financialEligibilityCriteria', { includePath: false })
      return item[key] === MAXIMUM_EFC
  })
  const enrollmentStatus = get(data,`json.${switchCase('eligibilityCriteria.0.enrollmentStatus')}`, null)

  fields['enrollmentStatus'] = enrollmentStatus
  fields['financialEligibilityMaximumEfc'] = maxEFC?.[switchCase('financialEligibilityAmount', { includePath: false })]
  fields['financialEligibilityMaximumAgi'] = maxAGI?.[switchCase('financialEligibilityAmount', { includePath: false })]
  fields['academicEligibilityMinClassRank'] = minClassRank?.[switchCase('academicEligibilityValue', { includePath: false })]
  fields['academicEligibilityMinActComposite'] = minOverallAct?.[switchCase('academicEligibilityValue', { includePath: false })]
  fields['academicEligibilityMinOverallSat'] = minOverallSat?.[switchCase('academicEligibilityValue', { includePath: false })]
  fields['academicEligibilityMinGPA'] = minGpa && parseFloat(minGpa?.[key]).toFixed(1)
  fields['id'] = data?.id

  return fields
}

export const defaultValues = {
  programOrganizationName: '',
  programName: '',
  scholarshipMaximumAward: '',
  scholarshipMinimumAward: '',
  programSelfDescription: '',
  numberOfScholarshipsAwarded: '',
  academicEligibilityMinGPA: '',
  programURL: '',
  applicationURL: '',
  isContest: null,
  isMeritBased: null,
  isNeedBased: null,
  openDate: null,
  closeDate: null,
  announcementDate: null,
  enrollmentStatus: '',
  academicEligibilityMinClassRank: '',
  academicEligibilityMinActComposite: '',
  academicEligibilityMinOverallSat: '',
  financialEligibilityMaximumAgi: '',
  financialEligibilityMaximumEfc: '',
}
